import { loadFont } from "@remotion/google-fonts/Poppins";
import { AbsoluteFill, Img } from "remotion";

// Only the weights these compositions actually use, and latin only. The unqualified call pulls
// every weight and subset — 54 network requests during a render that must not depend on the
// network being kind.
const { fontFamily } = loadFont("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

/**
 * The two still formats that accompany the reel. Rendered through Remotion rather than exported
 * from a design tool so they take the same props as the video: one dataset produces the whole set,
 * and a corrected number can never reach one asset but not another.
 *
 * Both are rendered with renderStill(), not renderMedia().
 */

/**
 * `sampleLabel` carries a volume figure and should only ever be given one above ~100k. At that
 * scale the number is the credibility ("923,000 planned stops" says we see the whole picture);
 * below it the number argues against itself, which is why the reel cites no sample size at all.
 */
export type NightsCardProps = {
	country: string;
	sampleLabel: string;
	rows: { name: string; nights: number; note?: string }[];
	photo: string;
};

const INK = "#0f3e4a";
const GREY = "#658c96";

/**
 * "How many nights do you actually need" — 1080x1350 feed card.
 *
 * The data sits on a solid panel rather than over the photograph. An earlier version put ranked
 * numbers straight onto the image and it was unreadable; a photo can carry a headline, but it
 * cannot carry a table.
 */
export const NightsCard: React.FC<NightsCardProps> = ({ country, sampleLabel, rows, photo }) => {
	// One hue in three steps, keyed to magnitude. The four-colour brand set fails a colourblind
	// check as a categorical palette, and a value printed on a #13c892 bar sits at 2.11:1 contrast —
	// so nights map to lightness steps and every number stays in ink beside its bar.
	const fill = (n: number) => (n >= 4 ? "#0b8f68" : n >= 3 ? "#13c892" : "#86e0c4");
	const max = Math.max(...rows.map((r) => r.nights));

	return (
		<AbsoluteFill style={{ fontFamily, backgroundColor: "#fff" }}>
			<div style={{ position: "relative", height: "40%", overflow: "hidden" }}>
				<Img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				<AbsoluteFill
					style={{ background: "linear-gradient(180deg, rgba(9,46,55,.58) 0%, rgba(9,46,55,.2) 45%, rgba(9,46,55,.82) 100%)" }}
				/>
				<AbsoluteFill style={{ padding: "62px 66px 54px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
					<div style={{ fontSize: 26, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "rgba(255,255,255,.88)" }}>
						{country} · {sampleLabel}
					</div>
					<div style={{ fontSize: 86, lineHeight: 1.06, fontWeight: 700, color: "#fff", letterSpacing: -1.5 }}>
						How many nights do you actually need?
					</div>
				</AbsoluteFill>
			</div>

			<div style={{ padding: "54px 66px", display: "flex", flexDirection: "column", gap: 30 }}>
				{rows.map((r) => (
					<div key={r.name} style={{ display: "grid", gridTemplateColumns: "300px 1fr 70px", alignItems: "center", gap: 26 }}>
						<div style={{ fontSize: 34, fontWeight: 500, color: INK }}>
							{r.name}
							{r.note ? <div style={{ fontSize: 22, color: GREY, fontWeight: 400 }}>{r.note}</div> : null}
						</div>
						<div style={{ height: 34, borderRadius: 9, background: "#edf5f7", overflow: "hidden" }}>
							<div style={{ width: `${(r.nights / max) * 100}%`, height: "100%", background: fill(r.nights), borderRadius: 9 }} />
						</div>
						<div style={{ fontSize: 44, fontWeight: 700, color: INK, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.nights}</div>
					</div>
				))}
			</div>
		</AbsoluteFill>
	);
};

export type RankedStoryProps = {
	eyebrow: string;
	headline: string;
	rows: { name: string; note?: string }[];
	photo: string;
	footnote?: string;
};

/**
 * "Most planned" — 1080x1920 story for a ranked list.
 *
 * Exists because `trending-story` writes `Rising now · {windowLabel}` and `+{growthPct}%` into the
 * markup, so it can only ever say that one thing. A ranking of what people plan is not growth and
 * rendering it through that composition would label the numbers as something they are not.
 *
 * Position is the whole claim here: no value column, because the counts behind these lists sit
 * below the 100k floor where a volume figure starts arguing against itself, and a share needs a
 * denominator that a story has no room to qualify. Rank survives without either.
 *
 * `note` is where a near-tie gets said out loud. Two items one plan apart are not first and second,
 * and printing them as such invents a gap the data does not have.
 *
 * Padding matches TrendingStory for the same reason: Instagram covers the first 250px and the last
 * 340px of the canvas, and the eyebrow carries the qualifier.
 */
export const RankedStory: React.FC<RankedStoryProps> = ({ eyebrow, headline, rows, photo, footnote }) => (
	<AbsoluteFill style={{ fontFamily, backgroundColor: "#092e37" }}>
		<Img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
		<AbsoluteFill
			style={{
				background: "linear-gradient(180deg, rgba(9,46,55,.22) 0%, rgba(9,46,55,.56) 42%, rgba(9,46,55,.95) 82%, #092e37 100%)",
			}}
		/>
		<AbsoluteFill style={{ padding: "320px 72px 420px", display: "flex", flexDirection: "column" }}>
			<div style={{ fontSize: 30, fontWeight: 600, letterSpacing: 4, textTransform: "uppercase", color: "#ffc14a" }}>{eyebrow}</div>
			<div style={{ fontSize: 96, lineHeight: 1.08, fontWeight: 700, color: "#fff", marginTop: 26, letterSpacing: -1.5 }}>{headline}</div>

			<div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
				{rows.map((r, i) => (
					<div
						key={r.name}
						style={{
							display: "flex",
							alignItems: "baseline",
							gap: 28,
							padding: "30px 0",
							borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,.18)",
						}}
					>
						<span style={{ fontSize: 46, fontWeight: 700, color: "#4fe3b4", fontVariantNumeric: "tabular-nums", minWidth: 74 }}>
							{String(i + 1).padStart(2, "0")}
						</span>
						<span style={{ display: "flex", flexDirection: "column" }}>
							<span style={{ fontSize: 58, fontWeight: 600, color: "#fff", lineHeight: 1.1 }}>{r.name}</span>
							{r.note ? <span style={{ fontSize: 30, fontWeight: 500, color: "#86e0c4", marginTop: 8 }}>{r.note}</span> : null}
						</span>
					</div>
				))}
			</div>

			{footnote ? <div style={{ fontSize: 26, fontWeight: 500, color: "rgba(255,255,255,.62)", marginTop: 34 }}>{footnote}</div> : null}
		</AbsoluteFill>
	</AbsoluteFill>
);

export type TrendingStoryProps = {
	headline: string;
	rows: { name: string; growthPct: number }[];
	photo: string;
	windowLabel: string;
};

/**
 * "Rising now" — 1080x1920 story.
 *
 * The measurement window lives in the eyebrow, not a footnote: a growth figure without its window
 * isn't a claim, but a caption-sized note under the numbers reads as a disclaimer on a poster.
 *
 * The top padding clears Instagram's own story chrome — progress bars, avatar, account name, close
 * button — which Meta documents as the first 250px of a 1920 canvas. The eyebrow used to sit at
 * 230px, inside that band, so the one element carrying the measurement window was the one the app
 * covered up.
 */
export const TrendingStory: React.FC<TrendingStoryProps> = ({ headline, rows, photo, windowLabel }) => (
	<AbsoluteFill style={{ fontFamily, backgroundColor: "#092e37" }}>
		<Img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
		<AbsoluteFill
			style={{
				background: "linear-gradient(180deg, rgba(9,46,55,.22) 0%, rgba(9,46,55,.56) 42%, rgba(9,46,55,.95) 82%, #092e37 100%)",
			}}
		/>
		<AbsoluteFill style={{ padding: "320px 72px 420px", display: "flex", flexDirection: "column" }}>
			<div style={{ fontSize: 30, fontWeight: 600, letterSpacing: 4, textTransform: "uppercase", color: "#ffc14a" }}>
				Rising now · {windowLabel}
			</div>
			<div style={{ fontSize: 96, lineHeight: 1.08, fontWeight: 700, color: "#fff", marginTop: 26, letterSpacing: -1.5 }}>{headline}</div>

			<div style={{ marginTop: "auto", display: "flex", flexDirection: "column" }}>
				{rows.map((r, i) => (
					<div
						key={r.name}
						style={{
							display: "flex",
							alignItems: "baseline",
							justifyContent: "space-between",
							padding: "34px 0",
							borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,.18)",
						}}
					>
						<span style={{ fontSize: 58, fontWeight: 600, color: "#fff" }}>{r.name}</span>
						<span style={{ fontSize: 66, fontWeight: 700, color: "#4fe3b4", fontVariantNumeric: "tabular-nums" }}>
							+{Math.round(r.growthPct)}%
						</span>
					</div>
				))}
			</div>
		</AbsoluteFill>
	</AbsoluteFill>
);
